import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import FunnelChart from "../components/FunnelChart";
import { api, type AbandonmentData, type FunnelData } from "../lib/api";
import { EmptyState } from "@/components/empty-state";
import { ErrorState, FormSection } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import { RangeSelect, useRangeParam } from "@/components/range-select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** Die Schrittnummern, die das Theme als `funnel_step` mitschickt. */
const STEP_NAMES: Record<string, string> = {
  "1": "1 · Foto",
  "2": "2 · Einwilligung",
  "3": "3 · Character",
  "4": "4 · Cover",
  "5": "5 · Extras",
  "6": "6 · Übersicht",
};

const ERROR_LABELS: Record<string, string> = {
  photo_rejected: "Foto abgelehnt (kein Bild)",
  photo_upload_failed: "Foto-Verarbeitung fehlgeschlagen",
  cart_add_failed: "Warenkorb-Aufruf fehlgeschlagen",
  addon_add_failed: "Add-on-Aufruf fehlgeschlagen",
};

export default function Funnel() {
  const [days, setDays] = useRangeParam(30);
  const [data, setData] = useState<FunnelData | null>(null);
  const [drop, setDrop] = useState<AbandonmentData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setData(null);
    setDrop(null);
    setError(null);
    const fail = (e: unknown) => setError((e as Error).message);
    api.funnel(days).then(setData).catch(fail);
    api.abandonment(days).then(setDrop).catch(fail);
  }, [days, reloadKey]);

  const totalAbandoned =
    drop?.by_step.reduce((n, s) => n + Number(s.abandoned), 0) ?? 0;

  return (
    <div>
      <PageHeader
        title="Funnel"
        actions={<RangeSelect value={days} onChange={setDays} />}
      />

      {error && (
        <ErrorState
          className="mb-4"
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      )}

      <div className="space-y-4">
        <FormSection
          title="Personalisierungs-Funnel"
          description="Sessions je Schritt; Prozent bezogen auf „Overlay geöffnet“."
        >
          {data ? (
            <FunnelChart data={data} />
          ) : (
            <Skeleton className="h-[440px] w-full" />
          )}
        </FormSection>

        <FormSection
          title="Wo Kunden aussteigen"
          description={
            drop
              ? `${totalAbandoned} ${totalAbandoned === 1 ? "Abbruch" : "Abbrüche"} im Zeitraum.`
              : undefined
          }
          actions={
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Was zählt als Abbruch?"
                >
                  <Info className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Als Abbruch zählt nur <code>flow_closed</code> mit{" "}
                <code>converted=false</code> – das Overlay schließt sich auch
                auf dem Weg in den Warenkorb.
              </TooltipContent>
            </Tooltip>
          }
          contentClassName="px-0"
        >
          {!drop ? (
            <Skeleton className="mx-4 h-32" />
          ) : drop.by_step.length === 0 ? (
            <EmptyState title="Keine Schließ-Events im Zeitraum" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Schritt</TableHead>
                  <TableHead className="text-right">Abgebrochen</TableHead>
                  <TableHead className="text-right">
                    Exit-Intent gezeigt
                  </TableHead>
                  <TableHead className="text-right">
                    Nach Kauf geschlossen
                  </TableHead>
                  <TableHead className="text-right">Ohne Kennzeichen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drop.by_step.map((s) => (
                  <TableRow key={s.funnel_step}>
                    <TableCell>
                      {STEP_NAMES[s.funnel_step] ?? s.funnel_step}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <strong>{s.abandoned}</strong>
                      {totalAbandoned > 0 && (
                        <span className="ml-1 text-muted-foreground">
                          {(
                            (Number(s.abandoned) / totalAbandoned) *
                            100
                          ).toFixed(0)}{" "}
                          %
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.prompted}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.converted}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {s.closed_unknown > 0 ? s.closed_unknown : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </FormSection>

        <FormSection title="Fehler unterwegs" contentClassName="px-0">
          {!drop ? (
            <Skeleton className="mx-4 h-24" />
          ) : drop.errors.length === 0 ? (
            <EmptyState title="Keine Fehler im Zeitraum" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Fehler</TableHead>
                  <TableHead>Detail</TableHead>
                  <TableHead className="text-right">Vorkommen</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drop.errors.map((e, i) => (
                  <TableRow key={`${e.event}-${e.reason}-${i}`}>
                    <TableCell>{ERROR_LABELS[e.event] ?? e.event}</TableCell>
                    <TableCell className="max-w-md truncate whitespace-normal">
                      {e.reason}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {e.count}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {e.sessions}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </FormSection>
      </div>
    </div>
  );
}
